// -----------------------------------------------------------------------------
// YesNoTogglePanel  (YES_NO_TOGGLE)
//
// Shows the action item's own question/label (item.title) + Yes / No buttons,
// plus an OPTIONAL note. On submit it (1) posts the note to the lead's notes —
// if one was typed — then (2) does the /complete/ POST with the answer, then
// calls onComplete(item) as a refresh signal. The orchestrator must not post
// completion again.
//
// Note posting mirrors LogCallPanel: POST /leads/{leadId}/notes/ { additional_notes }.
// Completion:
//   POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//   body { completion_state: 'YES', response_payload: { answer, note? } }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress, TextField } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

// The question/label is the action item's own configured text — show that.
function resolveQuestion(item) {
    const cfg = item?.raw?.action_config || item?.action_config || {};
    return (
        cfg.label || cfg.question || cfg.prompt || cfg.text
        || item?.title || item?.name || item?.description
        || 'Yes or no?'
    );
}

export default function YesNoTogglePanel({ item, practiceId, leadId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState('');
    const question = resolveQuestion(item);

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const handleAnswer = async (answer) => {
        if (submitting) return;
        if (!practiceId || !taskId || !itemId) {
            onComplete?.(item);
            return;
        }
        setSubmitting(true);
        try {
            const trimmedNote = note.trim();
            if (trimmedNote && leadId) {
                await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                    'POST',
                    { additional_notes: trimmedNote },
                );
            }
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                { completion_state: 'YES', response_payload: { answer, ...(trimmedNote ? { note: trimmedNote } : {}) } }
            );
            if (res.ok) {
                onComplete?.({ ...item, completionAlreadyPersisted: true });
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to save — please try again.' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || 'Failed to save — please try again.' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="yes-no-toggle-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
            }}
        >
            {/* Header: icon + the item's actual yes/no question */}
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                    sx={{
                        width: 40, height: 40, borderRadius: '11px', flexShrink: 0,
                        bgcolor: 'rgba(7,141,238,0.10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <Iconify icon="eva:toggle-right-outline" width={22} height={22} sx={{ color: '#078DEE' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Yes / No
                    </Typography>
                    <Typography data-testid="yes-no-toggle-question" sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                        {question}
                    </Typography>
                </Box>
            </Stack>

            {/* Optional note */}
            <TextField
                data-testid="yes-no-toggle-note"
                placeholder="Add a note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
            />

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1}>
                <Button
                    data-testid="yes-no-toggle-no"
                    variant="outlined"
                    fullWidth
                    disabled={submitting}
                    onClick={() => handleAnswer('no')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}
                >
                    No
                </Button>
                <Button
                    data-testid="yes-no-toggle-yes"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    onClick={() => handleAnswer('yes')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                >
                    Yes
                </Button>
            </Stack>

            <Button
                variant="text"
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', fontSize: '13px', color: '#6e6e73', alignSelf: 'center' }}
            >
                Cancel
            </Button>
        </Box>
    );
}

YesNoTogglePanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

YesNoTogglePanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    onComplete: null,
};
